import React, { useEffect, useMemo, useState } from 'react';

function normalize(value) {
  return String(value || '')
    .trim()
    .toLowerCase();
}

function messengerName(value) {
  const key = normalize(value);

  if (key.includes('telegram')) {
    return 'Telegram';
  }

  if (key.includes('whatsapp')) {
    return 'WhatsApp';
  }

  if (key.includes('max')) {
    return 'MAX';
  }

  return 'Личный аккаунт';
}

function statusStyle(tone) {
  if (tone === 'ok') {
    return {
      border: '#ABEFC6',
      background: '#ECFDF3',
      dot: '#17B26A',
      text: '#067647',
    };
  }

  if (tone === 'critical') {
    return {
      border: '#FECDCA',
      background: '#FEF3F2',
      dot: '#F04438',
      text: '#B42318',
    };
  }

  return {
    border: '#FEDF89',
    background: '#FFFAEB',
    dot: '#F79009',
    text: '#B54708',
  };
}

function serviceState(value) {
  const raw = (
    value
    && typeof value === 'object'
  )
    ? value.status
    : value;

  const key = normalize(raw);

  if (
    key === 'ok'
    || key === 'healthy'
    || key === 'ready'
    || key === 'active'
  ) {
    return {
      tone: 'ok',
      label: 'Работает',
    };
  }

  if (
    key === 'error'
    || key === 'failed'
    || key === 'unhealthy'
    || key === 'down'
  ) {
    return {
      tone: 'critical',
      label: 'Есть проблема',
    };
  }

  return {
    tone: 'warning',
    label: 'Проверяется',
  };
}

function sessionDiagnosis(session) {
  const status = normalize(session?.status);
  const live = normalize(session?.worker_status);
  const auth = normalize(session?.auth_state);
  const request = normalize(session?.auth_request);

  if (
    session?.needs_reauth
    || request
    || (
      auth
      && auth !== 'authorized'
      && auth !== 'authenticated'
      && auth !== 'active'
    )
  ) {
    return {
      tone: 'warning',
      label: 'Требуется авторизация',
      title: 'Подключение необходимо завершить',
      description:
        'Откройте подключение аккаунта и завершите авторизацию.',
    };
  }

  if (session?.restarting) {
    return {
      tone: 'warning',
      label: 'Перезапускается',
      title: 'Подключение восстанавливается',
      description:
        'Связка автоматически восстанавливает работу аккаунта.',
    };
  }

  if (
    live === 'connected'
    || live === 'active'
    || live === 'ready'
    || live === 'healthy'
  ) {
    return {
      tone: 'ok',
      label: 'Работает',
      title: 'Аккаунт подключён',
      description:
        'Подключение активно и готово к работе.',
    };
  }

  if (
    live === 'starting'
    || live === 'connecting'
    || status === 'starting'
    || status === 'connecting'
  ) {
    return {
      tone: 'warning',
      label: 'Подключается',
      title: 'Подключение запускается',
      description:
        'Дождитесь завершения запуска и обновите проверку.',
    };
  }

  if (
    live === 'banned'
    || live === 'error'
    || live === 'disconnected'
    || (
      !live
      && (
        status === 'active'
        || status === 'running'
        || status === 'connected'
      )
    )
  ) {
    return {
      tone: 'critical',
      label: 'Нет связи',
      title: 'Аккаунт сейчас не отвечает',
      description:
        'Связь с аккаунтом отсутствует. Требуется проверка подключения.',
    };
  }

  if (
    status === 'stopped'
    || status === 'inactive'
    || status === 'disabled'
  ) {
    return {
      tone: 'warning',
      label: 'Остановлено',
      title: 'Аккаунт не запущен',
      description:
        'Запустите подключение, если аккаунт должен использоваться.',
    };
  }

  if (session?.last_error) {
    return {
      tone: 'critical',
      label: 'Требуется внимание',
      title: 'Обнаружена ошибка подключения',
      description:
        'Обновите проверку. Если состояние сохранится, потребуется диагностика.',
    };
  }

  return {
    tone: 'warning',
    label: 'Состояние уточняется',
    title: 'Недостаточно данных',
    description:
      'Обновите проверку через несколько секунд.',
  };
}

function Card({
  title,
  state,
  description,
}) {
  const style = statusStyle(state.tone);

  return (
    <div
      style={{
        border: `1px solid ${style.border}`,
        background: style.background,
        borderRadius: 14,
        padding: 18,
        minHeight: 116,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
        }}
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: style.dot,
            flexShrink: 0,
          }}
        />

        <strong
          style={{
            color: '#101828',
            fontSize: 16,
          }}
        >
          {title}
        </strong>
      </div>

      <div
        style={{
          marginTop: 12,
          color: style.text,
          fontWeight: 700,
          fontSize: 15,
        }}
      >
        {state.label}
      </div>

      {description && (
        <div
          style={{
            marginTop: 7,
            color: '#667085',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          {description}
        </div>
      )}
    </div>
  );
}

function aggregateService(components, keys) {
  const states = keys
    .filter((key) => key in components)
    .map((key) => serviceState(components[key]));

  if (states.length === 0) {
    return null;
  }

  if (states.some((item) => item.tone === 'critical')) {
    return {
      tone: 'critical',
      label: 'Есть проблема',
    };
  }

  if (states.some((item) => item.tone === 'warning')) {
    return {
      tone: 'warning',
      label: 'Проверяется',
    };
  }

  return {
    tone: 'ok',
    label: 'Работает',
  };
}

export default function Health() {
  const [system, setSystem] = useState(null);
  const [bitrix, setBitrix] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [checkedAt, setCheckedAt] = useState(null);

  async function load() {
    setLoading(true);
    setError('');

    try {
      const [
        healthResponse,
        bitrixResponse,
        sessionsResponse,
      ] = await Promise.all([
        fetch('/health/full', {
          cache: 'no-store',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        }),
        fetch('/api/admin/bitrix/state', {
          cache: 'no-store',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        }),
        fetch('/api/admin/sessions', {
          cache: 'no-store',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
          },
        }),
      ]);

      const healthBody =
        await healthResponse
          .json()
          .catch(() => ({}));

      const bitrixBody =
        await bitrixResponse
          .json()
          .catch(() => ({}));

      const sessionsBody =
        await sessionsResponse
          .json()
          .catch(() => ([]));

      if (!healthResponse.ok) {
        throw new Error(
          'Не удалось проверить основные службы'
        );
      }

      if (!bitrixResponse.ok) {
        throw new Error(
          'Не удалось проверить связь с Битрикс24'
        );
      }

      if (!sessionsResponse.ok) {
        throw new Error(
          'Не удалось получить состояние подключений'
        );
      }

      setSystem(healthBody);
      setBitrix(bitrixBody);

      setSessions(
        Array.isArray(sessionsBody)
          ? sessionsBody
          : (
            Array.isArray(sessionsBody?.items)
              ? sessionsBody.items
              : []
          )
      );

      setCheckedAt(new Date());

    } catch (exception) {
      setSystem(null);
      setBitrix(null);
      setSessions([]);

      setError(
        exception?.message
        || 'Не удалось получить состояние системы'
      );

    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const diagnosed = useMemo(
    () => sessions.map((session) => ({
      session,
      state: sessionDiagnosis(session),
    })),
    [sessions]
  );

  const readyCount = diagnosed.filter(
    (item) => item.state.tone === 'ok'
  ).length;

  const warningCount = diagnosed.filter(
    (item) => item.state.tone === 'warning'
  ).length;

  const criticalCount = diagnosed.filter(
    (item) => item.state.tone === 'critical'
  ).length;

  const components = system?.components || {};

  const bitrixStatus = normalize(
    bitrix?.status
  );

  const bitrixState = !bitrix
    ? {
      tone: 'warning',
      label: 'Проверяется',
    }
    : bitrix?.connected
      ? {
        tone: 'ok',
        label: 'Работает',
      }
      : bitrix?.needs_reauth
        ? {
          tone: 'warning',
          label: 'Требуется авторизация',
        }
        : bitrixStatus === 'not_configured'
          ? {
            tone: 'warning',
            label: 'Не настроено',
          }
          : {
            tone: 'warning',
            label: 'Не подключено',
          };

  const coreState = aggregateService(
    components,
    ['redis', 'postgres']
  );

  const mediaState = aggregateService(
    components,
    ['s3', 'minio']
  );

  const serviceStates = [
    bitrixState,
    coreState,
    mediaState,
  ].filter(Boolean);

  const serviceCritical = serviceStates.some(
    (item) => item.tone === 'critical'
  );

  const serviceWarning = serviceStates.some(
    (item) => item.tone === 'warning'
  );

  const overallState = (
    criticalCount > 0
    || serviceCritical
  )
    ? {
      tone: 'critical',
      label: 'Есть проблемы',
    }
    : (
      warningCount > 0
      || serviceWarning
    )
      ? {
        tone: 'warning',
        label: 'Требуется внимание',
      }
      : {
        tone: 'ok',
        label: 'Система работает',
      };

  const overallDescription = diagnosed.length > 0
    ? (
      `Аккаунтов: ${diagnosed.length}. `
      + `Работают: ${readyCount}. `
      + (
        warningCount > 0
          ? `Требуют внимания: ${warningCount}. `
          : ''
      )
      + (
        criticalCount > 0
          ? `Есть проблемы: ${criticalCount}.`
          : ''
      )
    )
    : 'Личные аккаунты пока не добавлены.';

  return (
    <div
      style={{
        maxWidth: 1380,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 20,
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              marginBottom: 8,
            }}
          >
            Состояние системы
          </h1>

          <div
            style={{
              color: '#667085',
              lineHeight: 1.5,
            }}
          >
            Проверка личных аккаунтов и связи с Битрикс24
          </div>

          {checkedAt && (
            <div
              style={{
                color: '#98A2B3',
                fontSize: 13,
                marginTop: 6,
              }}
            >
              Проверено:{' '}
              {checkedAt.toLocaleTimeString(
                'ru-RU',
                {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                }
              )}
            </div>
          )}
        </div>

        <button
          onClick={load}
          disabled={loading}
        >
          {loading
            ? 'Проверяем…'
            : 'Обновить'}
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: 18,
            borderRadius: 14,
            border: '1px solid #FECDCA',
            background: '#FEF3F2',
            marginBottom: 24,
          }}
        >
          <strong
            style={{
              color: '#B42318',
            }}
          >
            Не удалось выполнить проверку
          </strong>

          <div
            style={{
              color: '#667085',
              marginTop: 7,
              lineHeight: 1.5,
            }}
          >
            {error}. Повторите проверку через несколько секунд.
          </div>
        </div>
      )}

      {!error && system && (
        <>
          <div
            style={{
              marginBottom: 28,
            }}
          >
            <Card
              title="Общее состояние"
              state={overallState}
              description={overallDescription}
            />
          </div>

          {serviceStates.length > 0 && (
            <section
              style={{
                marginBottom: 32,
              }}
            >
              <h2
                style={{
                  fontSize: 20,
                  marginBottom: 14,
                }}
              >
                Основные службы
              </h2>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(230px, 1fr))',
                  gap: 14,
                }}
              >
                {bitrixState && (
                  <Card
                    title="Связь с Битрикс24"
                    state={bitrixState}
                  />
                )}

                {coreState && (
                  <Card
                    title="Службы Связки"
                    state={coreState}
                  />
                )}

                {mediaState && (
                  <Card
                    title="Файлы и медиа"
                    state={mediaState}
                  />
                )}
              </div>
            </section>
          )}

          <section>
            <h2
              style={{
                fontSize: 20,
                marginBottom: 14,
              }}
            >
              Личные аккаунты
            </h2>

            {diagnosed.length === 0 ? (
              <div
                style={{
                  padding: 20,
                  border: '1px solid #EAECF0',
                  borderRadius: 14,
                  background: '#FFFFFF',
                  color: '#667085',
                }}
              >
                Личные аккаунты пока не добавлены.
              </div>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'repeat(auto-fit, minmax(280px, 1fr))',
                  gap: 14,
                }}
              >
                {diagnosed.map(
                  ({ session, state }, index) => {
                    const messenger =
                      messengerName(
                        session?.messenger_type
                      );

                    const account =
                      session?.phone_number
                      || `Аккаунт ${index + 1}`;

                    return (
                      <Card
                        key={
                          session?.id
                          || `${messenger}-${index}`
                        }
                        title={
                          `${messenger} · ${account}`
                        }
                        state={state}
                        description={
                          `${state.title}. ${state.description}`
                        }
                      />
                    );
                  }
                )}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
